import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Phone, PhoneOff, XCircle, CheckCircle2, MessageCircle, ExternalLink,
  RefreshCcw, ArrowUpDown, Sparkles, StickyNote, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  listConfirmationQueue,
  confirmOrder,
  markNoAnswer,
  cancelQueueOrder,
  addQueueNote,
  runQueueBackfill,
  rescoreQueue,
  RISK_COLORS,
  RISK_LABEL,
} from "@/lib/delivery";
import RiskSettingsDialog from "./RiskSettingsDialog";

const LEVEL_ORDER = ["all", "high", "medium", "low"];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("ro-RO", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      timeZone: "Europe/Bucharest",
    });
  } catch { return "—"; }
}

function fmtMoney(n, cur = "RON") {
  const v = Number(n) || 0;
  return `${v.toFixed(0)} ${cur}`;
}

function RiskBadge({ level, score }) {
  return (
    <Badge
      variant="outline"
      className={`${RISK_COLORS[level] || RISK_COLORS.low} px-1.5 py-0.5 text-[10px] font-semibold`}
      data-testid={`risk-badge-${level}`}
    >
      {RISK_LABEL[level] || "—"} · {score}
    </Badge>
  );
}

function ItemsPeek({ items }) {
  if (!items?.length) return <span className="text-xs text-muted-foreground">—</span>;
  const primary = items.filter((i) => !/livrare/i.test(i.title || ""))[0] || items[0];
  return (
    <span className="text-xs line-clamp-2 max-w-[280px]" title={items.map(i => `${i.quantity}× ${i.title}`).join("\n")}>
      {primary.quantity}× {primary.title}
      {items.length > 1 && (
        <span className="text-muted-foreground"> +{items.length - 1}</span>
      )}
    </span>
  );
}

export default function ConfirmationQueue({ shopDomain }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("pending");
  const [level, setLevel] = useState("all");
  const [sort, setSort] = useState("risk_desc");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null); // opened row
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listConfirmationQueue({ status, level, sort, search, limit: 100 });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setStats(data.stats || null);
    } catch (e) {
      toast.error("Nu am putut încărca coada: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [status, level, sort, search]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (row, action) => {
    setBusy(true);
    try {
      let fn = null;
      if (action === "confirm") fn = confirmOrder;
      else if (action === "no_answer") fn = markNoAnswer;
      else if (action === "cancel") fn = cancelQueueOrder;
      if (!fn) return;
      await fn(row.shopify_id);
      toast.success(
        action === "confirm" ? "Confirmată" :
        action === "no_answer" ? "Fără răspuns - rămâne în coadă" :
        "Comandă marcată anulată"
      );
      setDetail(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const openShopify = (row) => {
    const domain = shopDomain || "";
    if (!domain || !row?.order_id) return;
    const url = `https://${domain}/admin/orders/${row.order_id}`;
    window.open(url, "_blank", "noopener");
  };

  const runBackfill = async () => {
    setBusy(true);
    try {
      const r = await runQueueBackfill();
      toast.success(`Backfill: ${r.enqueued} noi, ${r.refreshed} actualizate, ${r.skipped_shipped} deja expediate`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const runRescore = async () => {
    setBusy(true);
    try {
      const r = await rescoreQueue();
      toast.success(`Recalculate ${r.updated} scoruri`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="confirmation-queue-page">
      {/* Header with counts */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Phone className="h-4 w-4 text-primary" />
            Coadă de confirmare COD
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Comenzi COD neexpediate, sortate după risc. Sună cu prioritate pe cele roșii.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="risk-settings-open-btn">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Setări scoring
          </Button>
          <Button variant="outline" size="sm" onClick={runRescore} disabled={busy} data-testid="rescore-all-btn">
            <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Recalculează scoruri
          </Button>
          <Button variant="outline" size="sm" onClick={runBackfill} disabled={busy} data-testid="backfill-btn">
            <ArrowUpDown className="h-3.5 w-3.5 mr-1.5" /> Rulează backfill
          </Button>
        </div>
      </div>

      {/* KPI cards for risk levels */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { key: "pending_high", label: "Risc ridicat", color: "border-rose-300 bg-rose-50", num: "text-rose-700" },
            { key: "pending_medium", label: "Risc mediu", color: "border-amber-300 bg-amber-50", num: "text-amber-800" },
            { key: "pending_low", label: "Risc scăzut", color: "border-emerald-200 bg-emerald-50", num: "text-emerald-700" },
            { key: "pending_total", label: "Total pending", color: "border-border bg-card", num: "" },
          ].map((c) => (
            <Card key={c.key} className={`${c.color} border`}>
              <CardContent className="p-3">
                <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{c.label}</div>
                <div className={`text-2xl font-bold mt-1 ${c.num}`} data-testid={`stat-${c.key}`}>
                  {stats[c.key]}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          placeholder="Caută nume, telefon, oraș, județ, #comandă"
          className="max-w-xs h-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="queue-search-input"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px] h-9" data-testid="queue-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">În așteptare</SelectItem>
            <SelectItem value="confirmed">Confirmate</SelectItem>
            <SelectItem value="cancelled">Anulate</SelectItem>
            <SelectItem value="all">Toate</SelectItem>
          </SelectContent>
        </Select>
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="w-[140px] h-9" data-testid="queue-level-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LEVEL_ORDER.map((l) => (
              <SelectItem key={l} value={l}>
                {l === "all" ? "Toate riscurile" : RISK_LABEL[l]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-[180px] h-9" data-testid="queue-sort-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="risk_desc">Risc descrescător</SelectItem>
            <SelectItem value="risk_asc">Risc crescător</SelectItem>
            <SelectItem value="newest">Comenzi noi</SelectItem>
            <SelectItem value="oldest">Comenzi vechi</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-auto">
          {loading ? "Se încarcă..." : `${total} rezultate`}
        </span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Risc</th>
                  <th className="text-left px-3 py-2 font-medium">Comandă</th>
                  <th className="text-left px-3 py-2 font-medium">Client</th>
                  <th className="text-left px-3 py-2 font-medium">Telefon</th>
                  <th className="text-left px-3 py-2 font-medium">Județ</th>
                  <th className="text-right px-3 py-2 font-medium">COD</th>
                  <th className="text-left px-3 py-2 font-medium">Produs</th>
                  <th className="text-left px-3 py-2 font-medium">Motive risc</th>
                  <th className="text-left px-3 py-2 font-medium">Încercări</th>
                  <th className="text-right px-3 py-2 font-medium">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={10} className="text-center py-10 text-sm text-muted-foreground">
                      Nu sunt comenzi în această categorie.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr
                    key={r.shopify_id}
                    className="border-t hover:bg-muted/30 cursor-pointer"
                    onClick={() => setDetail(r)}
                    data-testid={`queue-row-${r.shopify_id}`}
                  >
                    <td className="px-3 py-2"><RiskBadge level={r.risk_level} score={r.risk_score} /></td>
                    <td className="px-3 py-2 font-medium">{r.order_name || r.order_number || "—"}</td>
                    <td className="px-3 py-2">{r.customer_name || "—"}</td>
                    <td className="px-3 py-2 tabular-nums">{r.customer_phone || "—"}</td>
                    <td className="px-3 py-2 text-xs">{r.county || "—"}<span className="text-muted-foreground"> / {r.city || "—"}</span></td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(r.total, r.currency)}</td>
                    <td className="px-3 py-2"><ItemsPeek items={r.items} /></td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground max-w-[280px]">
                      {(r.risk_factors || []).slice(0, 2).map((f) => f.label).join(" · ") || "—"}
                      {(r.risk_factors || []).length > 2 && <span> …</span>}
                    </td>
                    <td className="px-3 py-2 text-center text-xs">
                      {r.attempts > 0 ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <PhoneOff className="h-3 w-3" />{r.attempts}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        {r.status === "pending" && (
                          <>
                            <Button
                              size="sm" variant="ghost"
                              className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
                              disabled={busy}
                              onClick={() => doAction(r, "confirm")}
                              title="Confirmată"
                              data-testid={`confirm-${r.shopify_id}`}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              className="h-7 px-2 text-amber-700 hover:bg-amber-50"
                              disabled={busy}
                              onClick={() => doAction(r, "no_answer")}
                              title="Fără răspuns"
                              data-testid={`noanswer-${r.shopify_id}`}
                            >
                              <PhoneOff className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              className="h-7 px-2 text-rose-700 hover:bg-rose-50"
                              disabled={busy}
                              onClick={() => doAction(r, "cancel")}
                              title="Anulează comanda"
                              data-testid={`cancel-${r.shopify_id}`}
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {shopDomain && (
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 px-2"
                            onClick={() => openShopify(r)}
                            title="Deschide în Shopify"
                            data-testid={`shopify-${r.shopify_id}`}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Detail sheet */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {detail.order_name}
                  <RiskBadge level={detail.risk_level} score={detail.risk_score} />
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <InfoRow label="Client" value={detail.customer_name} />
                  <InfoRow label="Telefon" value={detail.customer_phone} />
                  <InfoRow label="Județ" value={detail.county} />
                  <InfoRow label="Oraș" value={detail.city} />
                  <InfoRow label="COD" value={fmtMoney(detail.total, detail.currency)} />
                  <InfoRow label="Comandă" value={fmtDate(detail.order_created_at)} />
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-2 tracking-wide">
                    Motive de risc ({detail.risk_score}/100)
                  </div>
                  <div className="space-y-1">
                    {(detail.risk_factors || []).map((f, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <Badge
                          variant="outline"
                          className={
                            f.weight >= 25 ? "border-rose-300 text-rose-700 bg-rose-50" :
                            f.weight >= 10 ? "border-amber-300 text-amber-700 bg-amber-50" :
                            f.weight < 0 ? "border-emerald-300 text-emerald-700 bg-emerald-50" :
                            "border-slate-300 text-slate-600 bg-slate-50"
                          }
                        >
                          {f.weight > 0 ? "+" : ""}{f.weight}
                        </Badge>
                        <span className="flex-1 leading-relaxed">{f.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-2 tracking-wide">Produse</div>
                  <ul className="text-xs space-y-1">
                    {(detail.items || []).map((it, i) => (
                      <li key={i} className="flex justify-between">
                        <span>{it.quantity}× {it.title}</span>
                        <span className="text-muted-foreground">{it.price} lei</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {detail.notes?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase text-muted-foreground mb-2 tracking-wide flex items-center gap-1">
                      <StickyNote className="h-3 w-3" /> Note ({detail.notes.length})
                    </div>
                    <div className="space-y-2">
                      {detail.notes.map((n, i) => (
                        <div key={i} className="rounded-md border border-border bg-muted/30 p-2 text-xs">
                          <div className="text-muted-foreground text-[10px] mb-0.5">
                            {n.by} · {fmtDate(n.at)}
                          </div>
                          <div>{n.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <QueueNoteBox row={detail} onSaved={load} />

                {detail.status === "pending" && (
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => doAction(detail, "confirm")}
                      disabled={busy}
                      data-testid="sheet-confirm-btn"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Confirmată
                    </Button>
                    <Button
                      variant="outline"
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={() => doAction(detail, "no_answer")}
                      disabled={busy}
                      data-testid="sheet-noanswer-btn"
                    >
                      <PhoneOff className="h-4 w-4 mr-1" /> Fără răspuns
                    </Button>
                    <Button
                      variant="outline"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      onClick={() => doAction(detail, "cancel")}
                      disabled={busy}
                      data-testid="sheet-cancel-btn"
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Anulează
                    </Button>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {shopDomain && (
                    <Button variant="secondary" onClick={() => openShopify(detail)} data-testid="sheet-shopify-btn">
                      <ExternalLink className="h-4 w-4 mr-1" /> Shopify
                    </Button>
                  )}
                </div>
                {detail.status !== "pending" && (
                  <div className="text-xs text-muted-foreground pt-2 border-t">
                    Status curent: <span className="font-medium">{detail.status}</span>
                    {detail.last_action_by && (
                      <> · setat de {detail.last_action_by} la {fmtDate(detail.last_action_at)}</>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <RiskSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onSaved={() => { setSettingsOpen(false); load(); }}
      />
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</div>
      <div className="text-sm font-medium mt-0.5">{value || "—"}</div>
    </div>
  );
}

function QueueNoteBox({ row, onSaved }) {
  const [txt, setTxt] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!txt.trim()) return;
    setSaving(true);
    try {
      await addQueueNote(row.shopify_id, txt);
      setTxt("");
      onSaved?.();
      toast.success("Notă salvată");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };
  return (
    <div>
      <div className="text-xs uppercase text-muted-foreground mb-1.5 tracking-wide">Adaugă notă</div>
      <div className="flex gap-2">
        <Input value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="ex: promis să sune înapoi la 18:00" data-testid="queue-note-input" />
        <Button onClick={save} disabled={saving || !txt.trim()} size="sm" data-testid="queue-note-save-btn">Adaugă</Button>
      </div>
    </div>
  );
}
